import { FunctionalComponent, h } from "@stencil/core";

interface GuidedTourBackdropProps {
    targetRect: DOMRect | null;
}

interface ScreenRect {
    left: number;
    top: number;
    width: number;
    height: number;
}

interface BlockerRects {
    top: ScreenRect | null;
    bottom: ScreenRect | null;
    left: ScreenRect | null;
    right: ScreenRect | null;
}

const getBlockerRect = (
    targetRect: DOMRect,
    side: "top" | "bottom" | "left" | "right"
): ScreenRect | null => {
    if (targetRect === null || !getIsInViewport(targetRect)) return null;
    switch (side) {
        case "top":
            if (targetRect.bottom > 0 && targetRect.top < window.innerHeight) {
                return {
                    left: 0,
                    top: 0,
                    height: targetRect.top,
                    width: window.innerWidth,
                };
            }
            break;
        case "bottom":
            if (
                targetRect.bottom > 0 &&
                targetRect.top + targetRect.height < window.innerHeight
            ) {
                return {
                    left: 0,
                    top: targetRect.top + targetRect.height,
                    height:
                        window.innerHeight -
                        (targetRect.top + targetRect.height),
                    width: window.innerWidth,
                };
            }
            break;
        case "left":
            if (targetRect.left > 0) {
                return {
                    left: 0,
                    top: targetRect.top,
                    height: targetRect.height,
                    width: targetRect.left,
                };
            }
            break;
        case "right":
            if (targetRect.right < window.innerWidth) {
                return {
                    left: targetRect.right,
                    top: targetRect.top,
                    height: targetRect.height,
                    width: window.innerWidth - targetRect.right,
                };
            }
            break;
        default:
            break;
    }
    return null;
};

const getIsInViewport = (rect: DOMRect): boolean => {
    return !(rect.bottom < 0 || rect.top > window.innerHeight);
};

const getBlockerStyle = (
    screenRect: ScreenRect
): {
    [key: string]: string;
} => {
    return {
        top: `${screenRect.top}px`,
        left: `${screenRect.left}px`,
        width: `${screenRect.width}px`,
        height: `${screenRect.height}px`,
    };
};

export const GuidedTourBackdrop: FunctionalComponent<GuidedTourBackdropProps> = ({
    targetRect = null,
}) => {
    const baseClass = "ux-guided-tour-backdrop";
    const isInViewport =
        targetRect !== null ? getIsInViewport(targetRect) : false;
    const blockers: BlockerRects = {
        top: getBlockerRect(targetRect, "top"),
        bottom: getBlockerRect(targetRect, "bottom"),
        left: getBlockerRect(targetRect, "left"),
        right: getBlockerRect(targetRect, "right"),
    };
    return (
        <div
            class={{
                [baseClass]: true,
                [`${baseClass}--focus-rect`]: isInViewport,
            }}
        >
            {blockers.top !== null && (
                <div
                    class={{
                        [`${baseClass}__blocker`]: true,
                        [`${baseClass}__blocker--top`]: true,
                    }}
                    style={getBlockerStyle(blockers.top)}
                />
            )}
            {blockers.bottom !== null && (
                <div
                    class={{
                        [`${baseClass}__blocker`]: true,
                        [`${baseClass}__blocker--bottom`]: true,
                    }}
                    style={getBlockerStyle(blockers.bottom)}
                />
            )}
            {blockers.left !== null && (
                <div
                    class={{
                        [`${baseClass}__blocker`]: true,
                        [`${baseClass}__blocker--left`]: true,
                    }}
                    style={getBlockerStyle(blockers.left)}
                />
            )}
            {blockers.right !== null && (
                <div
                    class={{
                        [`${baseClass}__blocker`]: true,
                        [`${baseClass}__blocker--right`]: true,
                    }}
                    style={getBlockerStyle(blockers.right)}
                />
            )}
        </div>
    );
};

export default GuidedTourBackdrop;
